import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Bookmark,
  Briefcase,
  ChevronRight,
  GraduationCap,
  Globe2,
  Keyboard,
  Languages,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MoreVertical,
  Settings,
  Sparkles,
  Timer,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import 'flag-icons/css/flag-icons.min.css';

const EXAM_PATH = '/exam/b2';

const NAV = [
  { icon: LayoutDashboard, label: 'Ballina', path: '/dashboard' },
  { icon: MessageCircle, label: 'Biseda', path: '/chat' },
  { icon: BookOpen, label: 'Libri i Gramatikës', path: '/grammar' },
  { icon: Languages, label: 'Fjalor', path: '/vocabulary' },
  { icon: GraduationCap, label: 'Provimi Goethe B2', path: EXAM_PATH },
  { icon: Bookmark, label: 'Fjalë të Ruajtura', path: '/unknown-words' },
  { icon: Timer, label: 'Sfida e Kohës', path: '/timed-challenge' },
  { icon: Keyboard, label: 'Shtypi', path: '/typing-practice' },
  { icon: Sparkles, label: 'Ditari i Shkrimit', path: '/writing-journal' },
  { icon: Briefcase, label: 'Profesioni', path: '/profession' },
  { icon: Bookmark, label: 'Të Ruajturat', path: '/bookmarks' },
];

const isActive = (path, current) => (
  path === EXAM_PATH ? current.startsWith(EXAM_PATH) : current === path
);

const getUserDisplayName = (profile) => (
  [profile?.firstName || profile?.first_name, profile?.lastName || profile?.last_name]
    .filter(Boolean)
    .join(' ')
  || 'Student'
);

const Sidebar = ({ isOpen, onClose, isCollapsed }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const displayName = getUserDisplayName(user);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [accountMenuOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-zinc-950/30 lg:hidden ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'} transition-opacity duration-300`}
        onClick={onClose}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex transform flex-col text-sidebar-foreground transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'lg:w-[72px]' : 'lg:w-[236px]'} w-[236px]`}
      >
        <div className="mx-2 flex h-14 shrink-0 items-center justify-between border-b border-zinc-200/70 px-3 dark:border-zinc-700/50">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" onClick={onClose} className="flex items-center gap-2">
              <img src="/logo.png" alt="DeutschLernen" className={`${isCollapsed ? 'h-auto w-[178px] lg:h-8 lg:w-8 lg:object-cover lg:object-left' : 'h-auto w-[178px]'} max-w-full`} />
            </Link>
            <button type="button" onClick={onClose} className="rounded-lg p-1 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors lg:hidden">
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3 custom-scrollbar">
          <div className="space-y-0.5">
            {NAV.map(({ icon: Icon, label, path, expandable }) => {
              const active = isActive(path, location.pathname);
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={onClose}
                  title={isCollapsed ? label : undefined}
                  className={`group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ease-in-out outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring ${
                    active
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                  } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                >
                  <Icon size={18} strokeWidth={active ? 2.3 : 1.9} className={`transition-colors ${active ? 'text-sidebar-primary' : 'text-sidebar-foreground group-hover:text-sidebar-primary'}`} />
                  <span className={`min-w-0 flex-1 truncate ${isCollapsed ? 'lg:hidden' : ''}`}>{t(label)}</span>
                  {!isCollapsed && expandable && <ChevronRight size={15} className="text-sidebar-foreground/50 transition-transform group-hover:translate-x-0.5" />}
                </Link>
              );
            })}
          </div>

          {user?.role === 'admin' && (
            <div className="mt-4 border-t border-sidebar-border pt-3">
              <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/50 ${isCollapsed ? 'lg:hidden' : ''}`}>{t('Administrimi')}</p>
              <Link
                to="/admin"
                onClick={onClose}
                title={isCollapsed ? t('Paneli i Kontrollit') : undefined}
                className={`group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground transition-all duration-200 ease-in-out outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
              >
                <BarChart3 size={18} className="transition-colors group-hover:text-sidebar-primary" />
                <span className={isCollapsed ? 'lg:hidden' : ''}>{t('Paneli i Kontrollit')}</span>
              </Link>
            </div>
          )}
        </nav>

        <div className="relative mx-2 border-t border-zinc-200/70 p-2 dark:border-zinc-700/50" ref={accountMenuRef}>
          <button
            type="button"
            onClick={() => setAccountMenuOpen((open) => !open)}
            aria-label={t('Hap menynë e llogarisë')}
            aria-expanded={accountMenuOpen}
            className={`flex w-full items-center gap-2 rounded-xl p-2 text-left transition-colors hover:bg-sidebar-accent/50 ${isCollapsed ? 'lg:justify-center' : ''}`}
          >
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold ring-1 ring-zinc-200/70 dark:ring-zinc-700/50 ${user?.avatarUrl || user?.avatar_url ? 'bg-transparent' : 'bg-zinc-700 text-white'}`}>
              {user?.avatarUrl || user?.avatar_url ? (
                <img
                  src={user.avatarUrl || user.avatar_url}
                  alt={displayName}
                  className="h-full w-full bg-transparent object-contain"
                />
              ) : (
                displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
              )}
            </span>
            <span className={`min-w-0 flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>
              <strong className="block truncate text-xs text-sidebar-foreground">{displayName}</strong>
              <small className="block truncate text-[10px] text-sidebar-foreground/60">{user?.email || ''}</small>
            </span>
            <MoreVertical size={18} className={`shrink-0 text-sidebar-foreground/60 ${isCollapsed ? 'lg:hidden' : ''}`} />
          </button>
          {accountMenuOpen && (
            <section
              aria-label={t('Menuja e llogarisë')}
              className="absolute bottom-2 left-0 z-[60] w-full rounded-xl bg-[color:var(--popover)] text-popover-foreground shadow-2xl lg:left-full lg:ml-2 lg:w-[17rem]"
              style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.28)' }}
            >
              <div className="flex items-center gap-3 rounded-t-xl bg-muted/20 px-3 py-3">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold ${user?.avatarUrl || user?.avatar_url ? 'bg-transparent' : 'bg-zinc-700 text-white'}`}>
                  {user?.avatarUrl || user?.avatar_url ? (
                    <img src={user.avatarUrl || user.avatar_url} alt="" className="h-full w-full bg-transparent object-contain" />
                  ) : (
                    displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
                  )}
                </span>
                <span className="min-w-0">
                  <strong className="block truncate text-sm">{displayName}</strong>
                  <small className="block truncate text-xs text-muted-foreground">{user?.email || ''}</small>
                </span>
              </div>
              <div
                className="relative"
                onMouseEnter={() => setLanguageMenuOpen(true)}
                onMouseLeave={() => setLanguageMenuOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setLanguageMenuOpen(true)}
                  onFocus={() => setLanguageMenuOpen(true)}
                  aria-label={t('Zgjidh gjuhën')}
                  aria-expanded={languageMenuOpen}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                    languageMenuOpen ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                  }`}
                >
                  <Globe2 size={17} className="text-muted-foreground" />
                  <span className="flex-1">{t('Gjuha')}</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </button>
                {languageMenuOpen && (
                  <div
                    aria-label={t('Zgjidh gjuhën')}
                    className="absolute left-0 right-0 top-full z-[70] rounded-xl bg-[color:var(--popover)] p-1.5 text-popover-foreground shadow-2xl lg:left-full lg:right-auto lg:top-0 lg:w-[12rem]"
                    style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.38)' }}
                  >
                    {[
                      { code: 'sq', flag: 'al', label: 'Shqip' },
                      { code: 'de', flag: 'de', label: 'Gjermanisht' },
                    ].map(({ code, flag, label }) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          setLanguage(code);
                          setLanguageMenuOpen(false);
                        }}
                        aria-pressed={language === code}
                        className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                          language === code ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                        }`}
                      >
                        <span className={`fi fi-${flag}`} aria-label={label} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/settings"
                onClick={() => { setAccountMenuOpen(false); onClose(); }}
                className="flex items-center gap-3 border-b border-zinc-200/70 px-4 py-2.5 text-sm transition-colors hover:bg-zinc-100 dark:border-zinc-700/50 dark:hover:bg-zinc-800"
              >
                <Settings size={17} className="text-muted-foreground" />
                {t('Cilësimet')}
              </Link>
              <button
                type="button"
                onClick={() => { setAccountMenuOpen(false); logout(); onClose(); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <LogOut size={17} className="text-muted-foreground" />
                {t('Dil nga llogaria')}
              </button>
            </section>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
